import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import AccessControl from '../../components/AccessControl';
import './SurveyBuilder.css';
import config from '../../config';

const SurveyBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!id;

  // Get default folder path from navigation state (when creating from within a folder)
  const defaultFolderPath = location.state?.defaultFolderPath || [];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    questions: [],
    access: ['All'],
    folder_path: defaultFolderPath
  });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const createQuestion = (overrides = {}) => ({
    id: overrides.id || `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    text: '',
    type: 'multiple_choice',
    options: ['Option 1', 'Option 2'],
    scale_min: 1,
    scale_max: 5,
    scale_labels: { min: 'Not Satisfied', max: 'Very Satisfied' },
    response_length: 'medium', // For qualitative questions: short, medium, long
    max_selections: null, // For multi_select questions: max number of selections (null = no limit)
    json_schema: { type: 'object', properties: { choice: { type: 'string' } } },
    attachments: [], // Image/PDF attachments (up to 5)
    ...overrides
  });

  const [draftQuestion, setDraftQuestion] = useState(createQuestion());

  useEffect(() => {
    if (isEditMode) {
      fetchSurvey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const fetchSurvey = async () => {
    try {
      const res = await fetch(`${config.api.baseUrl}/synthetic-audience/surveys/${id}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch survey');

      const data = await res.json();
      setFormData({
        title: data.survey.title || '',
        description: data.survey.description || '',
        questions: data.survey.questions || [],
        access: data.survey.access && data.survey.access.length > 0 ? data.survey.access : ['All'],
        folder_path: data.survey.folder_path || []
      });
    } catch (err) {
      console.error('Error fetching survey:', err);
      setError('Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 'multiple_choice':
        return 'Multiple Choice';
      case 'yes_no':
        return 'Yes/No';
      case 'rating_scale':
        return 'Rating Scale';
      case 'multi_select':
        return 'Multi-Select';
      case 'qualitative':
        return 'Qualitative';
      default:
        return 'Question';
    }
  };

  const updateQuestionWithSchema = (question, field, value) => {
    const updated = { ...question, [field]: value };
    const shouldUpdateSchema = ['type', 'options', 'scale_min', 'scale_max', 'max_selections'].includes(field);

    if (shouldUpdateSchema) {
      if (updated.type === 'multiple_choice' || updated.type === 'yes_no') {
        const opts = updated.type === 'yes_no' ? ['Yes', 'No'] : updated.options;
        updated.json_schema = {
          type: 'object',
          properties: {
            choice: { type: 'string', enum: opts }
          }
        };
      } else if (updated.type === 'rating_scale') {
        updated.json_schema = {
          type: 'object',
          properties: {
            rating: { type: 'number', minimum: updated.scale_min, maximum: updated.scale_max }
          }
        };
      } else if (updated.type === 'multi_select') {
        const choicesSchema = {
          type: 'array',
          items: { type: 'string', enum: updated.options },
          uniqueItems: true
        };
        const maxSel = updated.max_selections;
        if (maxSel && Number.isInteger(maxSel) && maxSel > 0) {
          choicesSchema.maxItems = maxSel;
        }
        updated.json_schema = {
          type: 'object',
          properties: { choices: choicesSchema },
          required: ['choices'],
          additionalProperties: false
        };
      }
    }

    return updated;
  };

  const resetDraftQuestion = () => {
    setDraftQuestion(createQuestion());
    setEditingIndex(null);
  };

  const updateDraftQuestion = (field, value) => {
    setDraftQuestion(prev => updateQuestionWithSchema(prev, field, value));
  };

  const addDraftOption = () => {
    const newOptions = [...draftQuestion.options, `Option ${draftQuestion.options.length + 1}`];
    updateDraftQuestion('options', newOptions);
  };

  const removeDraftOption = (optionIndex) => {
    if (draftQuestion.options.length <= 2) {
      alert('Must have at least 2 options');
      return;
    }
    const newOptions = draftQuestion.options.filter((_, i) => i !== optionIndex);
    updateDraftQuestion('options', newOptions);
  };

  const updateDraftOption = (optionIndex, value) => {
    const newOptions = [...draftQuestion.options];
    newOptions[optionIndex] = value;
    updateDraftQuestion('options', newOptions);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const currentAttachments = draftQuestion.attachments || [];
    if (currentAttachments.length + files.length > 5) {
      alert('Maximum 5 files allowed per question');
      e.target.value = '';
      return;
    }

    const newAttachments = [];
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        alert(`File "${file.name}" is not an image or PDF. Only images and PDFs are allowed.`);
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`File "${file.name}" exceeds 10MB limit`);
        continue;
      }

      try {
        // Convert to base64 data URL
        const reader = new FileReader();
        const dataUrl = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Upload file to server
        const uploadRes = await fetch(`${config.api.baseUrl}/synthetic-audience/attachments/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            file: {
              name: file.name,
              type: file.type,
              content: dataUrl,
              size: file.size
            }
          })
        });

        if (!uploadRes.ok) {
          const errorData = await uploadRes.json();
          throw new Error(errorData.error || 'Upload failed');
        }

        const uploadData = await uploadRes.json();

        // Store the server URL and file info
        newAttachments.push({
          name: file.name,
          type: file.type,
          url: `${config.api.baseUrl}${uploadData.url}`,  // Full URL for accessing the file
          content: dataUrl,  // Keep base64 for preview in builder only
          size: file.size
        });
      } catch (err) {
        console.error(`Error uploading file ${file.name}:`, err);
        alert(`Failed to upload file "${file.name}": ${err.message}`);
      }
    }

    if (newAttachments.length > 0) {
      updateDraftQuestion('attachments', [...currentAttachments, ...newAttachments]);
    }

    e.target.value = '';
  };

  const removeAttachment = (index) => {
    const newAttachments = draftQuestion.attachments.filter((_, i) => i !== index);
    updateDraftQuestion('attachments', newAttachments);
  };

  const addOrUpdateQuestion = () => {
    if (!draftQuestion.text.trim()) {
      alert('Please enter question text');
      return;
    }
    if ((draftQuestion.type === 'multiple_choice' || draftQuestion.type === 'multi_select') && draftQuestion.options.length < 2) {
      alert('Multiple choice questions must have at least 2 options');
      return;
    }

    if (editingIndex !== null) {
      setFormData(prev => {
        const updated = [...prev.questions];
        updated[editingIndex] = draftQuestion;
        return { ...prev, questions: updated };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        questions: [...prev.questions, draftQuestion]
      }));
    }

    resetDraftQuestion();
  };

  const moveQuestion = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setFormData(prev => {
      const updated = [...prev.questions];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return { ...prev, questions: updated };
    });
  };

  const handleDragStart = (index) => (e) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    const fromIndex = draggingIndex ?? parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === null) return;
    moveQuestion(fromIndex, index);
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
    if (editingIndex === index) {
      resetDraftQuestion();
    }
  };

  const handleEditQuestion = (index) => {
    const questionCopy = JSON.parse(JSON.stringify(formData.questions[index]));
    setDraftQuestion(questionCopy);
    setEditingIndex(index);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a survey title');
      return;
    }

    if (formData.questions.length === 0) {
      alert('Please add at least one question');
      return;
    }

    // Validate questions
    for (const q of formData.questions) {
      if (!q.text.trim()) {
        alert('All questions must have text');
        return;
      }
      if ((q.type === 'multiple_choice' || q.type === 'multi_select') && q.options.length < 2) {
        alert('Multiple choice questions must have at least 2 options');
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      const url = isEditMode
        ? `${config.api.baseUrl}/synthetic-audience/surveys/${id}`
        : `${config.api.baseUrl}/synthetic-audience/surveys`;

      const method = isEditMode ? 'PUT' : 'POST';

      // Prepare data for saving - remove base64 content from attachments to avoid bloating database
      const dataToSave = {
        ...formData,
        questions: formData.questions.map(q => ({
          ...q,
          attachments: q.attachments?.map(att => ({
            name: att.name,
            type: att.type,
            url: att.url,
            size: att.size
            // Omit 'content' field which has base64 for preview
          }))
        }))
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSave)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save survey');
      }

      if (isEditMode) {
        alert('Survey saved successfully');
      } else {
        navigate('/synthetic-audience-workshop');
      }
    } catch (err) {
      console.error('Error saving survey:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="survey-builder-loading">
          <div className="survey-builder-loading-content">
            <div className="survey-builder-loading-spinner"></div>
            <div className="survey-builder-loading-text">Loading survey...</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="survey-builder">
      <header className="builder-header">
        <div>
          <h1>{isEditMode ? 'Edit Survey' : 'Create New Survey'}</h1>
          <p className="header-subtitle">
            Build a survey to gather insights from your synthetic personas
          </p>
        </div>
        <button className="btn-back" onClick={() => navigate('/synthetic-audience-workshop')}>
          ← Back
        </button>
      </header>

      {error && <div className="error-banner">{error}</div>}

      <div className="builder-form">
        {/* Basic Info */}
        <section className="form-section">
          <h2>Basic Information</h2>
          <div className="form-group">
            <label>Survey Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Customer Satisfaction Survey"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this survey..."
              rows="3"
            />
          </div>
          <p className="section-note">
            Surveys are templates that can be run multiple times with different personas
          </p>
          <div className="form-group">
            <label>Survey Access</label>
            <AccessControl
              value={formData.access || ['All']}
              onChange={(accessList) => setFormData(prev => ({ ...prev, access: accessList }))}
            />
          </div>
        </section>

        {/* Questions */}
        <section className="form-section">
          <div className="section-header">
            <h2>Questions</h2>
            <span className="section-note">Drag cards to reorder</span>
          </div>

          <div className="question-editor">
            <div className="question-editor-header">
              <h3>{editingIndex !== null ? 'Edit Question' : 'Add a Question'}</h3>
              {editingIndex !== null && (
                <button type="button" className="btn-secondary" onClick={resetDraftQuestion}>
                  Cancel Edit
                </button>
              )}
            </div>

            <div className="form-group">
              <label>Question Text *</label>
              <textarea
                value={draftQuestion.text}
                onChange={(e) => updateDraftQuestion('text', e.target.value)}
                placeholder="Enter your question..."
                rows="2"
              />
            </div>

            <div className="form-group">
              <label>Question Type</label>
              <select
                value={draftQuestion.type}
                onChange={(e) => updateDraftQuestion('type', e.target.value)}
              >
                <option value="multiple_choice">Multiple Choice</option>
                <option value="yes_no">Yes/No</option>
                <option value="rating_scale">Rating Scale (1-10)</option>
                <option value="multi_select">Multi-Select</option>
                <option value="qualitative">Qualitative (Free Text)</option>
              </select>
            </div>

            {/* File Attachments */}
            <div className="form-group">
              <label>Attachments (Optional)</label>
              <small className="help-text">Add images or PDFs to provide visual context (Max 5 files, 10MB each)</small>

              <input
                type="file"
                id="question-file-upload"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <label htmlFor="question-file-upload" className="btn-upload-files">
                + Add Images/PDFs
              </label>

              {draftQuestion.attachments && draftQuestion.attachments.length > 0 && (
                <div className="attachments-list">
                  {draftQuestion.attachments.map((attachment, idx) => (
                    <div key={idx} className="attachment-item">
                      {attachment.type.startsWith('image/') ? (
                        <img
                          src={attachment.content}
                          alt={attachment.name}
                          className="attachment-preview"
                        />
                      ) : (
                        <div className="attachment-preview pdf-preview">
                          <span className="pdf-icon">📄</span>
                          <span className="pdf-name">{attachment.name}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn-remove-attachment"
                        onClick={() => removeAttachment(idx)}
                        title="Remove attachment"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(draftQuestion.type === 'multiple_choice' || draftQuestion.type === 'multi_select') && (
              <div className="options-section">
                <label>Options</label>
                {draftQuestion.options.map((option, oIdx) => (
                  <div key={oIdx} className="option-row">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateDraftOption(oIdx, e.target.value)}
                      placeholder={`Option ${oIdx + 1}`}
                    />
                    <button
                      className="btn-remove-option"
                      onClick={() => removeDraftOption(oIdx)}
                      disabled={draftQuestion.options.length <= 2}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button className="btn-add-option" onClick={addDraftOption}>
                  + Add Option
                </button>
              </div>
            )}

            {draftQuestion.type === 'multi_select' && (
              <div className="form-group">
                <label>Max Selections (Optional)</label>
                <small className="help-text">Limit how many options respondents can choose. Leave empty for no limit.</small>
                <input
                  type="number"
                  value={draftQuestion.max_selections || ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : parseInt(e.target.value);
                    updateDraftQuestion('max_selections', val);
                  }}
                  min="1"
                  max={draftQuestion.options.length}
                  placeholder={`1 - ${draftQuestion.options.length}`}
                  style={{ width: '120px' }}
                />
              </div>
            )}

            {draftQuestion.type === 'rating_scale' && (
              <div className="scale-config">
                <div className="scale-row">
                  <div className="form-group">
                    <label>Min Value</label>
                    <input
                      type="number"
                      value={draftQuestion.scale_min}
                      onChange={(e) => updateDraftQuestion('scale_min', parseInt(e.target.value))}
                      min="1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Value</label>
                    <input
                      type="number"
                      value={draftQuestion.scale_max}
                      onChange={(e) => updateDraftQuestion('scale_max', parseInt(e.target.value))}
                      min="2"
                      max="10"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Min Label</label>
                  <input
                    type="text"
                    value={draftQuestion.scale_labels?.min || ''}
                    onChange={(e) => updateDraftQuestion('scale_labels', {
                      ...draftQuestion.scale_labels,
                      min: e.target.value
                    })}
                    placeholder="e.g., Not Satisfied"
                  />
                </div>
                <div className="form-group">
                  <label>Max Label</label>
                  <input
                    type="text"
                    value={draftQuestion.scale_labels?.max || ''}
                    onChange={(e) => updateDraftQuestion('scale_labels', {
                      ...draftQuestion.scale_labels,
                      max: e.target.value
                    })}
                    placeholder="e.g., Very Satisfied"
                  />
                </div>
              </div>
            )}

            {draftQuestion.type === 'qualitative' && (
              <div className="response-length-config">
                <div className="form-group">
                  <label>Response Length</label>
                  <div className="length-toggle-container">
                    <button
                      type="button"
                      className={`length-toggle-btn ${(draftQuestion.response_length || 'medium') === 'short' ? 'active' : ''}`}
                      onClick={() => updateDraftQuestion('response_length', 'short')}
                    >
                      Short
                    </button>
                    <button
                      type="button"
                      className={`length-toggle-btn ${(draftQuestion.response_length || 'medium') === 'medium' ? 'active' : ''}`}
                      onClick={() => updateDraftQuestion('response_length', 'medium')}
                    >
                      Medium
                    </button>
                    <button
                      type="button"
                      className={`length-toggle-btn ${(draftQuestion.response_length || 'medium') === 'long' ? 'active' : ''}`}
                      onClick={() => updateDraftQuestion('response_length', 'long')}
                    >
                      Long
                    </button>
                  </div>
                  <small className="help-text">
                    Short: 1 sentence • Medium: 2-3 sentences • Long: 4+ sentences
                  </small>
                </div>
              </div>
            )}

            <div className="question-editor-actions">
              <button type="button" className="btn-add" onClick={addOrUpdateQuestion}>
                {editingIndex !== null ? 'Update Question' : '+ Add Question'}
              </button>
            </div>
          </div>

          {formData.questions.length === 0 ? (
            <p className="empty-message">No questions yet. Add one above to get started.</p>
          ) : (
            <div className="question-list">
              {formData.questions.map((question, qIdx) => (
                <div
                  key={question.id || `q-${qIdx}`}
                  className={`question-card compact ${draggingIndex === qIdx ? 'dragging' : ''} ${dragOverIndex === qIdx ? 'drag-over' : ''}`}
                  draggable
                  onDragStart={handleDragStart(qIdx)}
                  onDragOver={handleDragOver(qIdx)}
                  onDrop={handleDrop(qIdx)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="question-card-main">
                    <div className="question-title">
                      <span className="question-number">Question {qIdx + 1}</span>
                      <span className="question-type">{getQuestionTypeLabel(question.type)}</span>
                    </div>
                    <div className="question-text-preview">
                      {question.text?.trim() || 'Untitled question'}
                    </div>
                    <div className="question-meta">
                      {question.type === 'rating_scale' && (
                        <span>Scale {question.scale_min}-{question.scale_max}</span>
                      )}
                      {(question.type === 'multiple_choice' || question.type === 'multi_select') && (
                        <span>{question.options?.length || 0} options</span>
                      )}
                      {question.type === 'yes_no' && <span>2 options</span>}
                      {question.type === 'qualitative' && (
                        <span>Free text ({question.response_length || 'medium'} response)</span>
                      )}
                    </div>
                  </div>
                  <div className="question-card-actions">
                    <button className="btn-secondary" onClick={() => handleEditQuestion(qIdx)}>
                      Edit
                    </button>
                    <button className="btn-remove" onClick={() => removeQuestion(qIdx)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Actions */}
        <div className="form-actions">
          <button
            className="btn-cancel"
            onClick={() => navigate('/synthetic-audience-workshop')}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Survey'}
          </button>
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default SurveyBuilder;
